import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Plus, ShieldCheck, MessageSquare, CheckCircle2,
  AlertCircle, ArrowRight, Download, Share, Smartphone,
  Cpu, Lightbulb, Home as HomeIcon, LineChart, User, LogOut,
} from 'lucide-react';
import EnergyDashboard from '@/components/customer-app/EnergyDashboard';
import AppBottomNav, { APP_TABS } from '@/components/customer-app/AppBottomNav';
import AppSidebar from '@/components/customer-app/AppSidebar';
import QuotesTab from '@/components/customer-app/QuotesTab';
import DeviceSuggestionsPanel from '@/components/customer-app/DeviceSuggestionsPanel';
import DeviceSetupWizard from '@/components/customer-app/DeviceSetupWizard';
import OptimalPlanCTA from '@/components/customer-app/OptimalPlanCTA';
import MyDeviceDataPanel from '@/components/customer-app/MyDeviceDataPanel';
import PilotOptInCard from '@/components/customer-app/PilotOptInCard';
import AppOnboardingSetup, { AppOnboardingBanner, isSetupCompleted } from '@/components/customer-app/AppOnboardingSetup';
import PowersureDeviceGridSection from '@/components/powersure-ui/PowersureDeviceGridSection';
import FeatureBullet from '@/components/powersure-ui/FeatureBullet';
import { Sparkles, Plug, Sun, Flame, BatteryCharging } from 'lucide-react';
import { categoryLabel } from '@/lib/deviceLabels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PILOT_TOKEN_KEY = 'powersure_pilot_token';

function deviceType() {
  const w = window.innerWidth;
  if (w < 640) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

async function logEvent(event, extra = {}) {
  const token = localStorage.getItem(PILOT_TOKEN_KEY);
  if (!token) return;
  try {
    await axios.post(`${API}/pilot-devices/activity`,
      { event, route: window.location.pathname,
        device_type: deviceType(),
        browser: navigator.userAgent.slice(0, 80),
        ...extra },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 });
  } catch (_) { /* swallow */ }
}

export function PilotLandingPage() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('redeeming');
  const [error, setError] = useState('');

  useEffect(() => {
    const tokenInUrl = params.get('t');
    if (tokenInUrl) {
      localStorage.setItem(PILOT_TOKEN_KEY, tokenInUrl);
      logEvent('pilot_link_opened');
    }
    axios.post(`${API}/pilot-devices/redeem`, { code: code?.toUpperCase() })
      .then((r) => {
        const tok = r.data?.pilot_token;
        if (tok) localStorage.setItem(PILOT_TOKEN_KEY, tok);
        setStatus('ok');
        logEvent('pilot_code_verified');
        setTimeout(() => navigate('/app', { replace: true }), 800);
      })
      .catch((e) => {
        setError(e?.response?.data?.detail?.error || 'onbekende fout');
        setStatus('error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(180deg, #0a1410 0%, #050a08 100%)' }}>
      <div
        className="max-w-md w-full rounded-2xl border border-emerald-400/30 shadow-2xl p-6 text-center space-y-3"
        style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.20) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
        data-testid="pilot-landing">
        {status === 'redeeming' && (
          <>
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-300" />
            <p className="text-sm text-white/70">Pilotcode controleren&hellip;</p>
          </>
        )}
        {status === 'ok' && (
          <>
            <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto" />
            <p className="text-base text-white">Welkom bij <strong>Mijn Powersure</strong></p>
            <p className="text-sm text-white/65">Een ogenblik&hellip;</p>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="w-10 h-10 text-rose-300 mx-auto" />
            <p className="font-semibold text-white">Pilotcode niet geldig</p>
            <p className="text-sm text-white/65">{error}</p>
            <button onClick={() => navigate('/')}
              className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-md font-semibold transition-colors">
              Naar homepage
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function SliderRow({ label, value, onChange, name }) {
  return (
    <div>
      <label className="text-sm font-medium text-white/85">{label}: <strong className="text-emerald-300">{value}/10</strong></label>
      <input type="range" min="1" max="10" value={value}
        data-testid={`pilot-fb-${name}`}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mt-1 accent-emerald-400" />
    </div>
  );
}

function RadioRow({ label, name, value, options, onPick }) {
  return (
    <div>
      <div className="text-sm font-medium mb-1.5 text-white/85">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button key={opt} onClick={() => onPick(name, opt)}
            data-testid={`pilot-fb-${name}-${opt}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${value === opt
              ? 'bg-emerald-500 text-emerald-950 border-emerald-400 font-semibold'
              : 'bg-white/5 border-white/15 text-white/80 hover:bg-white/10 hover:border-emerald-400/40'}`}>
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function FeedbackForm({ pilotType, onDone }) {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    clarity_score: 8,
    device_add_success: 'ja',
    recognition_match: 'ja',
    privacy_trust_score: 8,
    understood_no_auto_control: 'ja',
    unclear_text: '',
    missing_feature: '',
    would_use: 'misschien',
    mkb_devices_mentioned: '',
    mkb_main_problem: '',
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem(PILOT_TOKEN_KEY);
      const payload = { ...form };
      if (pilotType !== 'MKB') {
        delete payload.mkb_devices_mentioned;
        delete payload.mkb_main_problem;
      } else {
        if (!payload.mkb_devices_mentioned) delete payload.mkb_devices_mentioned;
        if (!payload.mkb_main_problem) delete payload.mkb_main_problem;
      }
      if (!payload.unclear_text) delete payload.unclear_text;
      if (!payload.missing_feature) delete payload.missing_feature;
      await axios.post(`${API}/pilot-devices/feedback`, payload,
        { headers: { Authorization: `Bearer ${token}` } });
      setDone(true);
      logEvent('feedback_submitted');
      if (onDone) onDone();
    } catch (e) {
      setError(e?.response?.data?.detail?.error || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-2xl border border-emerald-400/30 shadow-2xl p-6 text-center"
        style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.20) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
        data-testid="pilot-feedback-thanks">
        <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto mb-2" />
        <h3 className="font-semibold text-lg text-white" style={{ fontFamily: 'Outfit' }}>Bedankt voor uw feedback</h3>
        <p className="text-sm text-white/70 mt-1">
          Powersure gebruikt uw antwoorden om Mijn Powersure te verbeteren.
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl border border-white/10 shadow-2xl p-5 sm:p-6 space-y-4"
      style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
      data-testid="pilot-feedback-form">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
            <MessageSquare className="w-4 h-4" />
          </span>
          <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-300/90 font-semibold">Uw feedback</span>
        </div>
        <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Outfit' }}>Help ons Mijn Powersure verbeteren</h2>
        <p className="text-sm text-white/70 mt-1.5">
          Een paar korte vragen — 2 minuten.
        </p>
      </div>
      <SliderRow label="Was duidelijk wat u moest doen?" name="clarity_score"
        value={form.clarity_score} onChange={(v) => set('clarity_score', v)} />
      <RadioRow label="Lukte het om een apparaat toe te voegen?"
        name="device_add_success" value={form.device_add_success}
        options={['ja', 'nee']} onPick={set} />
      <RadioRow label="Klopte de herkenning ongeveer?"
        name="recognition_match" value={form.recognition_match}
        options={['ja', 'nee', 'deels']} onPick={set} />
      <SliderRow label="Vertrouwde u de privacy-uitleg?" name="privacy_trust_score"
        value={form.privacy_trust_score}
        onChange={(v) => set('privacy_trust_score', v)} />
      <RadioRow label="Begreep u dat automatische sturing uit staat?"
        name="understood_no_auto_control" value={form.understood_no_auto_control}
        options={['ja', 'nee']} onPick={set} />
      <div>
        <label className="text-sm font-medium text-white/85">Wat was onduidelijk?</label>
        <textarea value={form.unclear_text}
          data-testid="pilot-fb-unclear"
          onChange={(e) => set('unclear_text', e.target.value.slice(0, 600))}
          rows={2}
          className="w-full mt-1.5 bg-white/5 border border-white/15 rounded-lg p-2.5 text-sm text-white placeholder-white/35 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400/50" />
      </div>
      <div>
        <label className="text-sm font-medium text-white/85">Wat mist u nog?</label>
        <textarea value={form.missing_feature}
          data-testid="pilot-fb-missing"
          onChange={(e) => set('missing_feature', e.target.value.slice(0, 600))}
          rows={2}
          className="w-full mt-1.5 bg-white/5 border border-white/15 rounded-lg p-2.5 text-sm text-white placeholder-white/35 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400/50" />
      </div>
      <RadioRow label="Zou u dit willen gebruiken als energie-app?"
        name="would_use" value={form.would_use}
        options={['ja', 'misschien', 'nee']} onPick={set} />
      {pilotType === 'MKB' && (
        <>
          <div>
            <label className="text-sm font-medium text-white/85">
              Welke apparaten/systemen gebruikt uw bedrijf die Powersure ook zou moeten herkennen?
            </label>
            <textarea value={form.mkb_devices_mentioned}
              data-testid="pilot-fb-mkb-devices"
              onChange={(e) => set('mkb_devices_mentioned', e.target.value.slice(0, 600))}
              rows={2}
              className="w-full mt-1.5 bg-white/5 border border-white/15 rounded-lg p-2.5 text-sm text-white placeholder-white/35 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400/50" />
          </div>
          <div>
            <label className="text-sm font-medium text-white/85">Grootste energieprobleem?</label>
            <select value={form.mkb_main_problem}
              data-testid="pilot-fb-mkb-problem"
              onChange={(e) => set('mkb_main_problem', e.target.value)}
              className="w-full mt-1.5 bg-white/5 border border-white/15 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400/50">
              <option value="" className="bg-[#0e1b16]">&mdash; kies &mdash;</option>
              <option value="piekverbruik" className="bg-[#0e1b16]">piekverbruik</option>
              <option value="zonnepanelen" className="bg-[#0e1b16]">zonnepanelen</option>
              <option value="laadpunten" className="bg-[#0e1b16]">laadpunten</option>
              <option value="koeling_airco" className="bg-[#0e1b16]">koeling/airco</option>
              <option value="noodstroom" className="bg-[#0e1b16]">noodstroom</option>
              <option value="kosten" className="bg-[#0e1b16]">kosten</option>
              <option value="inzicht" className="bg-[#0e1b16]">inzicht</option>
              <option value="anders" className="bg-[#0e1b16]">anders</option>
            </select>
          </div>
        </>
      )}
      {error && (
        <div className="text-xs text-rose-200 bg-rose-500/10 border border-rose-400/30 rounded-md p-2.5"
          data-testid="pilot-fb-error">
          {error}
        </div>
      )}
      <button onClick={submit} disabled={submitting}
        data-testid="pilot-fb-submit"
        className="w-full px-4 py-3 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-xl font-semibold transition-colors disabled:opacity-50 shadow-lg shadow-emerald-900/40">
        {submitting ? 'Versturen\u2026' : 'Feedback versturen'}
      </button>
    </div>
  );
}

function friendlyCategory(cat) {
  return categoryLabel(cat) || 'Apparaat';
}

function friendlyStatus(d) {
  if (d.pending_review) return { label: 'Controle nodig', tone: 'amber' };
  if (d.installer_required) return { label: 'Installateur nodig', tone: 'blue' };
  if (d.control_status === 'control_not_supported') return { label: 'Alleen meten', tone: 'slate' };
  return { label: 'Herkend \u00b7 sturing uit', tone: 'emerald' };
}

function todayDutch() {
  try {
    return new Date().toLocaleDateString('nl-NL', {
      weekday: 'long', day: 'numeric', month: 'long',
    });
  } catch {
    return 'vandaag';
  }
}

export default function MijnPowersurePage() {
  const navigate = useNavigate();
  // Iter 78 — Customer auth gate. Iter 80 P0 hardening:
  // STRICT priority:
  //   1. Customer JWT in localStorage → call /me (with silent refresh
  //      via the customerApi interceptor). If valid → CustomerAppShell.
  //      If invalid → redirect to /customer/login. We NEVER fall through
  //      to the pilot/demo branch when a customer session was attempted,
  //      otherwise the page would silently render "Hoi Owner Demo" for
  //      paying customers whose access token had expired.
  //   2. No customer JWT AND a pilot token → legacy demo/pilot branch.
  //   3. Neither → show "log in" panel.
  const [customerAuth, setCustomerAuth] = useState({ checked: false, user: null, hadToken: false });
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const tok = localStorage.getItem('powersure_customer_token');
        if (!tok) { if (active) setCustomerAuth({ checked: true, user: null, hadToken: false }); return; }
        const { customerApi } = await import('@/hooks/useCustomerAuth');
        const r = await customerApi.get('/customer/auth/me');
        if (active) setCustomerAuth({ checked: true, user: r.data, hadToken: true });
      } catch {
        // Customer attempted to authenticate but token + refresh both
        // failed. Wipe stale local cache so the user lands on login.
        try {
          localStorage.removeItem('powersure_customer_token');
          localStorage.removeItem('powersure_customer_user');
        } catch { /* ignore */ }
        if (active) setCustomerAuth({ checked: true, user: null, hadToken: true });
      }
    })();
    return () => { active = false; };
  }, []);

  const [pilot, setPilot] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [tab, setTab] = useState('home');
  const [devices, setDevices] = useState([]);
  const [feedbackDone, setFeedbackDone] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);
  const [installSuppressed, setInstallSuppressed] = useState(false);
  const [setupDone, setSetupDone] = useState(false);
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    // Swap manifest to /app variant for proper PWA install
    let link = document.querySelector("link[rel='manifest']");
    const original = link?.getAttribute('href');
    if (link) link.setAttribute('href', '/manifest-app.json');
    return () => { if (link && original) link.setAttribute('href', original); };
  }, []);

  useEffect(() => {
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (standalone) setIsInstalled(true);

    const ua = window.navigator.userAgent || '';
    const isIos = /iPhone|iPad|iPod/.test(ua);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    // Samsung Internet and other non-Chrome Android browsers can trigger
    // Google Play Protect's "Onveilige app geblokkeerd" warning when the
    // WebAPK install path is used from a non-Play-Store origin. We
    // intentionally suppress the auto install-prompt on those browsers and
    // only show the manual "via browsermenu" instruction instead.
    const isSamsungInternet = /SamsungBrowser/i.test(ua);
    const isAndroidWebView = /Android/i.test(ua) && /(wv|Version\/[\d.]+\s*Chrome\/[\d.]+\s*Mobile\s*Safari)/i.test(ua);
    const suppressInstallPrompt = isSamsungInternet || isAndroidWebView;
    const dismissed = localStorage.getItem('powersure_ios_hint_dismissed') === '1';
    if (isIos && isSafari && !standalone && !dismissed) setShowIosHint(true);

    if (suppressInstallPrompt) {
      // Browser is on the Play-Protect-risk list. We do NOT register a
      // beforeinstallprompt listener; user keeps the manual instruction.
      setInstallSuppressed(true);
      return;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      logEvent('pwa_install_available');
    };
    const onInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
      logEvent('pwa_installed');
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    if (!installPrompt) return;
    logEvent('pwa_install_clicked');
    installPrompt.prompt();
    try {
      const choice = await installPrompt.userChoice;
      logEvent('pwa_install_choice', { outcome: choice?.outcome });
    } catch (_) { /* noop */ }
    setInstallPrompt(null);
  };

  const dismissIosHint = () => {
    localStorage.setItem('powersure_ios_hint_dismissed', '1');
    setShowIosHint(false);
  };

  useEffect(() => {
    const token = localStorage.getItem(PILOT_TOKEN_KEY);
    if (!token) { setAuthChecked(true); return; }
    axios.get(`${API}/pilot-devices/me`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setPilot(r.data?.pilot);
        setFeedbackDone(!!r.data?.pilot?.feedback_received);
        setAuthChecked(true);
        logEvent('app_opened');
      })
      .catch(() => {
        localStorage.removeItem(PILOT_TOKEN_KEY);
        setAuthChecked(true);
      });
  }, []);

  useEffect(() => {
    if (!pilot) return;
    const token = localStorage.getItem(PILOT_TOKEN_KEY);
    axios.get(`${API}/customer/device-onboarding/profiles`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setDevices(r.data?.items || []))
      .catch(() => setDevices([]));
  }, [pilot]);

  // P5 Flow-fix: zet setup pas in app — toon onboarding sheet bij eerste open
  useEffect(() => {
    if (!pilot) return;
    const done = isSetupCompleted();
    setSetupDone(done);
    if (!done) {
      // Iets vertraging zodat de header eerst rendert
      const t = window.setTimeout(() => setShowSetup(true), 400);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [pilot]);

  const changeTab = (next) => {
    setTab(next);
    logEvent('app_tab_change', { tab: next });
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // Iter 78 — Customer auth has top priority over pilot/demo
  if (!customerAuth.checked) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #0a1410 0%, #050a08 100%)' }}>
      <Loader2 className="w-6 h-6 animate-spin text-emerald-300" />
    </div>;
  }
  if (customerAuth.user) {
    const CustomerAppShell = require('@/components/customer-app/CustomerAppShell').default;
    return <CustomerAppShell
      user={customerAuth.user}
      onLogout={async () => {
        const { customerLogout } = await import('@/hooks/useCustomerAuth');
        await customerLogout();
        navigate('/customer/login', { replace: true });
      }}
    />;
  }

  // Iter 80 P0: customer attempted auth but token+refresh failed → ALWAYS
  // route to /customer/login. Never bleed into pilot/demo branch.
  if (customerAuth.hadToken) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(180deg, #0a1410 0%, #050a08 100%)' }}>
        <div className="max-w-md w-full text-center space-y-4 rounded-2xl border border-white/10 shadow-2xl p-6"
          style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
          data-testid="mijn-powersure-session-expired">
          <h1 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Mijn Powersure</h1>
          <p className="text-white/70">Uw sessie is verlopen. Log opnieuw in om verder te gaan.</p>
          <div className="flex flex-col gap-2 pt-2">
            <Link to="/customer/login" className="inline-block px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-md font-semibold transition-colors" data-testid="mp-go-login-expired">
              Opnieuw inloggen
            </Link>
            <Link to="/customer/forgot-password" className="text-emerald-300 hover:text-emerald-200 text-sm">Wachtwoord vergeten?</Link>
          </div>
        </div>
      </div>
    );
  }

  // No customer login + no pilot token + checks completed → redirect to login
  if (authChecked && !pilot) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(180deg, #0a1410 0%, #050a08 100%)' }}>
        <div className="max-w-md w-full text-center space-y-4 rounded-2xl border border-white/10 shadow-2xl p-6"
          style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
          data-testid="mijn-powersure-need-account">
          <h1 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Mijn Powersure</h1>
          <p className="text-white/70">Log in op uw klantaccount om Mijn Powersure te openen.</p>
          <div className="flex flex-col gap-2 pt-2">
            <Link to="/customer/login" className="inline-block px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-md font-semibold transition-colors" data-testid="mp-go-login">
              Inloggen
            </Link>
            <Link to="/customer/forgot-password" className="text-emerald-300 hover:text-emerald-200 text-sm">Wachtwoord vergeten?</Link>
            <Link to="/" className="text-white/55 hover:text-white/85 text-sm">Terug naar homepage</Link>
          </div>
          <p className="text-[11px] text-white/45 pt-3">
            Pilot/demo? Open via{' '}
            <Link to="/admin?tab=pilot_devices" className="text-emerald-300 underline">Admin → Pilot Access</Link>.
          </p>
        </div>
      </div>
    );
  }

  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #0a1410 0%, #050a08 100%)' }}>
      <Loader2 className="w-6 h-6 animate-spin text-emerald-300" />
    </div>;
  }

  if (!pilot) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(180deg, #0a1410 0%, #050a08 100%)' }}>
        <div className="max-w-md w-full text-center space-y-4 rounded-2xl border border-white/10 shadow-2xl p-6"
          style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
          data-testid="mijn-powersure-need-pilot">
          <h1 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Mijn Powersure</h1>
          <p className="text-white/70">
            Heeft u een pilotcode van Powersure ontvangen? Open deze app via uw
            persoonlijke Powersure-link in WhatsApp of e-mail.
          </p>
          <p className="text-xs text-white/55">
            Bent u eigenaar of beheerder? Open de demo via{' '}
            <Link to="/admin?tab=pilot_devices" className="text-emerald-300 underline hover:text-emerald-200">
              Admin → Pilot Access
            </Link>.
          </p>
          <Link to="/" className="inline-block px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-md font-semibold transition-colors">
            Terug naar homepage
          </Link>
        </div>
      </div>
    );
  }

  const pendingReview = devices.filter((d) => d.pending_review).length;
  const installerRequired = devices.filter((d) => d.installer_required).length;
  const hasP1 = devices.some((d) => d.category === 'A_p1_meter');

  return (
    <AppSidebar tab={tab} onChange={changeTab}>
    <div className="min-h-screen pb-24 md:pb-8" style={{ background: 'transparent' }} data-testid="mijn-powersure-page">
      {/* App header — dark glass */}
      <header className="bg-[#0e1b16]/80 backdrop-blur-md border-b border-white/10 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 pl-14 md:pl-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-base sm:text-lg font-semibold tracking-tight text-white">
                Mijn Powersure
              </h1>
              <p className="text-[11px] sm:text-xs text-white/55 capitalize">
                {todayDutch()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline-flex items-center gap-1 text-[11px] bg-emerald-500/15 text-emerald-300 px-2 py-1 rounded-full border border-emerald-400/30">
                <ShieldCheck className="w-3 h-3" /> Sturing uit
              </span>
              <button
                onClick={() => { localStorage.removeItem(PILOT_TOKEN_KEY); navigate('/'); }}
                data-testid="mp-header-logout"
                aria-label="Uitloggen"
                className="md:hidden text-white/55 hover:text-emerald-300"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
          {/* Desktop / tablet top tabs */}
          <nav className="hidden md:flex gap-1 pt-3 -mb-px overflow-x-auto"
            data-testid="mp-nav">
            {APP_TABS.map((t) => (
              <button key={t.id} onClick={() => changeTab(t.id)}
                data-testid={`mp-nav-${t.id}`}
                className={`px-3 py-1.5 text-sm rounded-t-md border-b-2 whitespace-nowrap transition-colors ${tab === t.id
                  ? 'border-emerald-400 text-emerald-300 font-medium'
                  : 'border-transparent text-white/60 hover:text-white'}`}>
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-4 pb-6 space-y-4">
        {tab === 'home' && (
          <div className="space-y-4" data-testid="mp-tab-home">
            {/* P5: Vrijblijvende setup-banner (alleen als setup nog niet voltooid) */}
            {!setupDone && (
              <AppOnboardingBanner
                onOpen={() => setShowSetup(true)}
                testId="mp-app-onboarding-banner"
              />
            )}
            {/* Greeting hero — dark Powersure card */}
            <section
              className="rounded-2xl border border-white/10 shadow-2xl p-5 sm:p-6 relative overflow-hidden"
              style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.20) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
              data-testid="mijn-powersure-welcome"
            >
              <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-emerald-300/90 font-semibold">Welkom</p>
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-0.5 text-white" style={{ fontFamily: 'Outfit' }}>
                Hoi {pilot.name}
              </h2>
              <p className="text-sm text-white/75 mt-1.5 leading-relaxed">
                Powersure helpt u uw apparaten en stroomverbruik slimmer te begrijpen.
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] sm:text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 px-2.5 py-1 rounded-full">
                <ShieldCheck className="w-3 h-3" />
                Automatische sturing staat uit
              </div>
            </section>

            {/* PWA install banner — dark */}
            {installPrompt && !isInstalled && (
              <div className="rounded-2xl bg-white/5 backdrop-blur-sm border border-emerald-400/30 p-4 flex items-center justify-between gap-3"
                data-testid="mp-install-banner">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center shrink-0">
                    <Download className="w-5 h-5 text-emerald-300" />
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm">Installeer Mijn Powersure</div>
                    <div className="text-xs text-white/55">Zet de app op uw beginscherm.</div>
                  </div>
                </div>
                <button onClick={triggerInstall}
                  data-testid="mp-install-banner-btn"
                  className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-full text-sm font-semibold transition-colors whitespace-nowrap">
                  Installeer
                </button>
              </div>
            )}
            {showIosHint && !isInstalled && (
              <div className="rounded-2xl bg-sky-500/10 border border-sky-400/30 p-4"
                data-testid="mp-ios-install-hint">
                <div className="flex items-start gap-3">
                  <Smartphone className="w-5 h-5 text-sky-300 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-semibold text-white text-sm">Installeer op uw iPhone of iPad</div>
                    <p className="text-sm text-white/75 mt-1 leading-relaxed">
                      Tik op de <Share className="w-4 h-4 inline align-text-bottom" /> deel-knop onderaan
                      Safari en kies <strong className="text-white">&ldquo;Zet op beginscherm&rdquo;</strong>.
                    </p>
                  </div>
                  <button onClick={dismissIosHint}
                    data-testid="mp-ios-hint-dismiss"
                    className="text-xs text-sky-300 hover:text-sky-200 underline whitespace-nowrap">Sluit</button>
                </div>
              </div>
            )}

            {/* P0 visueel concept: status → top-3 kansen → apparaten-grid → grafiek lager */}

            {/* TOP 3 KANSEN — neutrale status-badges (geen ongefundeerde € claims) */}
            <section data-testid="mp-top3-kansen" className="space-y-2">
              <div className="flex items-center gap-1.5 mb-1">
                <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
                <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-300/90">Top 3 kansen</span>
              </div>
              {[
                { icon: Flame, title: 'Boiler slim aansturen', sub: 'Slimme kans', badge: 'Aanbevolen', tone: 'emerald', testId: 'mp-kans-boiler' },
                { icon: Sun, title: 'Warmtepomp optimaliseren', sub: 'Eerst meten', badge: 'Eerst meten', tone: 'sky', testId: 'mp-kans-warmtepomp' },
                { icon: BatteryCharging, title: 'Auto laden met zonnestroom', sub: 'Volgende stap', badge: 'Later plannen', tone: 'amber', testId: 'mp-kans-laden' },
              ].map(({ icon: Icon, title, sub, badge, tone, testId }) => {
                const badgeCls = {
                  emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
                  sky:     'bg-sky-500/15 text-sky-300 border-sky-400/30',
                  amber:   'bg-amber-500/15 text-amber-300 border-amber-400/30',
                }[tone];
                return (
                  <div
                    key={testId}
                    data-testid={testId}
                    className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm px-3.5 py-2.5"
                  >
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 shrink-0">
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-white leading-tight">{title}</div>
                      <div className="text-[11px] text-white/55 mt-0.5">{sub}</div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${badgeCls}`}>
                      {badge}
                    </span>
                  </div>
                );
              })}
              <p className="text-[10px] text-white/40 leading-snug px-1 pt-0.5">
                Volgorde op verwachte impact — concrete besparing volgt na koppeling van uw apparaten.
              </p>
            </section>

            {/* APPARATEN-GRID — pictogrammen als kernwaarde (concept-aligned) */}
            <div data-testid="mp-home-device-grid-wrap">
              <PowersureDeviceGridSection
                onAddClick={() => { logEvent('device_onboarding_started'); navigate('/onboarding/devices'); }}
                title="Apparaten koppelen"
                subtitle="Powersure ondersteunt deze apparaten. Tik om de status te zien."
              />
            </div>

            {/* Energy dashboard — verplaatst naar onder als ondersteunend blok */}
            <EnergyDashboard compact />

            {/* Devices summary card — dark */}
            <section className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-4 sm:p-5"
              data-testid="mp-devices-summary">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-base font-semibold tracking-tight text-white">Uw apparaten</h3>
                  <p className="text-xs text-white/55 mt-0.5">
                    {devices.length === 0
                      ? 'Nog geen apparaten toegevoegd'
                      : `${devices.length} apparaat${devices.length === 1 ? '' : 'ten'} toegevoegd`}
                    {pendingReview > 0 ? ` \u00b7 ${pendingReview} wacht op controle` : ''}
                  </p>
                </div>
                <button onClick={() => changeTab('apparaten')}
                  data-testid="mp-devices-view-all"
                  className="text-xs text-emerald-300 font-medium hover:text-emerald-200">
                  Bekijk alle &rarr;
                </button>
              </div>
              <button onClick={() => { logEvent('device_onboarding_started');
                                        navigate('/onboarding/devices'); }}
                data-testid="mijn-powersure-add-device"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-xl p-4 flex items-center justify-between transition-colors shadow-lg shadow-emerald-900/40">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-950/20 flex items-center justify-center">
                    <Plus className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="text-base font-semibold">Apparaat toevoegen</div>
                    <div className="text-xs opacity-80">Maak een foto van het typeplaatje</div>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5" />
              </button>
            </section>

            {/* Action cards — dark */}
            <section className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              data-testid="mp-action-cards">
              {!hasP1 && (
                <div className="rounded-2xl bg-white/5 border border-white/10 p-4"
                  data-testid="mp-action-p1">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-sky-500/20 text-sky-300 border border-sky-400/30 flex items-center justify-center">
                      <LineChart className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm text-white">Koppel later uw P1-meter</h4>
                      <p className="text-xs text-white/60 mt-0.5">
                        Voor echte data per uur. We laten u weten wanneer dat kan.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="rounded-2xl bg-white/5 border border-white/10 p-4"
                data-testid="mp-action-control-off">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-white">Sturing staat uit</h4>
                    <p className="text-xs text-white/60 mt-0.5">
                      Powersure schakelt nu nog niets. Alleen met uw aparte toestemming.
                    </p>
                  </div>
                </div>
              </div>
              {!feedbackDone && (
                <button onClick={() => { changeTab('feedback'); logEvent('feedback_started'); }}
                  data-testid="mijn-powersure-feedback-btn"
                  className="text-left rounded-2xl bg-white/5 border border-white/10 hover:bg-white/8 hover:border-emerald-400/30 p-4 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-500/20 text-amber-300 border border-amber-400/30 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm text-white">Feedback geven</h4>
                      <p className="text-xs text-white/60 mt-0.5">Korte vragen, 2 minuten.</p>
                    </div>
                  </div>
                </button>
              )}
              {installerRequired > 0 && (
                <div className="rounded-2xl bg-white/5 border border-white/10 p-4"
                  data-testid="mp-action-installer">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-sky-500/20 text-sky-300 border border-sky-400/30 flex items-center justify-center">
                      <Cpu className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm text-white">Installateur nodig</h4>
                      <p className="text-xs text-white/60 mt-0.5">
                        {installerRequired} apparaat{installerRequired === 1 ? '' : 'ten'} hoeft een vakman aan te sluiten.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {tab === 'apparaten' && (
          <div className="space-y-4" data-testid="mp-tab-apparaten">
            {/* Fase 3.3 wizard + plan-CTA */}
            <DeviceSetupWizard
              sessionId={(typeof window !== 'undefined' && window.localStorage?.getItem('ps_session_id')) || undefined}
            />
            <OptimalPlanCTA
              detectedDevices={(devices || []).map((d) => ({
                device_id: d.profile_id,
                device_kind: d.category || 'unknown',
              }))}
              sessionId={(typeof window !== 'undefined' && window.localStorage?.getItem('ps_session_id')) || undefined}
            />

            {/* P0 concept-aligned: zelfde pictogram-grid als home — geen list */}
            <PowersureDeviceGridSection
              onAddClick={() => navigate('/onboarding/devices')}
              title="Mijn apparaten"
              subtitle="Koppel apparaten om beter advies te krijgen. Veilige read-only start — later uit te breiden."
              testId="mp-apparaten-grid"
            />

            {/* Eigen toegevoegde apparaten — lijst als dat al gevuld is */}
            {devices.length > 0 && (
              <section className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-4 sm:p-5">
                <h3 className="text-base font-semibold text-white mb-3" style={{ fontFamily: 'Outfit' }}>Door u toegevoegd</h3>
                <ul className="space-y-2">
                  {devices.map((d) => {
                    const s = friendlyStatus(d);
                    const toneClass = {
                      emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
                      amber:   'bg-amber-500/15 text-amber-300 border-amber-400/30',
                      blue:    'bg-sky-500/15 text-sky-300 border-sky-400/30',
                      slate:   'bg-white/8 text-white/65 border-white/15',
                    }[s.tone];
                    return (
                      <li key={d.profile_id}
                        data-testid={`mp-device-${d.profile_id}`}
                        className="rounded-xl bg-white/5 border border-white/10 p-3">
                        <div className="flex justify-between items-start gap-3">
                          <div className="min-w-0">
                            <div className="font-medium truncate text-white text-sm">{d.brand || 'Onbekend merk'} {d.model || ''}</div>
                            <div className="text-xs text-white/55">{friendlyCategory(d.category)}</div>
                          </div>
                          <span className={`text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${toneClass}`}>
                            {s.label}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </div>
        )}

        {tab === 'offertes' && (
          <div className="space-y-4" data-testid="mp-tab-offertes">
            <QuotesTab />
          </div>
        )}

        {tab === 'inzicht' && (
          <div className="space-y-4" data-testid="mp-tab-inzicht">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Outfit' }}>Inzicht</h2>
            <EnergyDashboard />
            {!hasP1 && (
              <div className="rounded-2xl bg-sky-500/10 border border-sky-400/30 p-4 text-sm text-white/85"
                data-testid="mp-p1-hint">
                Tip: een slimme meter (P1) geeft later beter inzicht in uw verbruik per uur en fase.
              </div>
            )}
          </div>
        )}

        {tab === 'advies' && (
          <div className="space-y-4" data-testid="mp-tab-advies">
            <DeviceSuggestionsPanel sessionId={(typeof window !== 'undefined' && window.localStorage?.getItem('ps_session_id')) || undefined} />
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                  <Lightbulb className="w-4 h-4" />
                </span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-emerald-300/90 font-semibold">Mijn advies</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Outfit' }}>Wat te doen, in volgorde</h2>
              <p className="text-sm text-white/65 mt-1">
                Korte stappen die het meeste opleveren. Geen techniek-taal, geen verplichtingen.
              </p>
            </div>
            {[
              { t: 'Voeg eerst uw grootste verbruikers toe',
                d: 'Airco, warmtepomp, droger en oven. Hierop valt het meeste te winnen.',
                Icon: Plus, badge: 'Aanbevolen', badgeTone: 'emerald' },
              { t: 'Een slimme meter (P1) geeft beter inzicht',
                d: 'Daarmee kan Powersure later verbruik per uur en per fase volgen.',
                Icon: LineChart, badge: 'Eerst meten', badgeTone: 'sky' },
              { t: 'Sturing staat uit tot u apart toestemming geeft',
                d: 'Powersure schakelt nu nog niets — alleen herkennen en meten waar mogelijk.',
                Icon: ShieldCheck, badge: 'Veilig', badgeTone: 'emerald' },
              { t: 'Voor vaste installaties is een installateur nodig',
                d: 'Zonnepanelen, omvormer, warmtepomp of laadpaal. Powersure regelt de match.',
                Icon: Cpu, badge: 'Installateur nodig', badgeTone: 'blue' },
              { t: 'Bij twijfel kijken we het later beter na',
                d: 'Onduidelijke apparaten zetten we op een handmatige controlelijst.',
                Icon: Lightbulb, badge: 'Later', badgeTone: 'amber' },
            ].map((c, i) => {
              const badgeCls = {
                emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
                sky:     'bg-sky-500/15 text-sky-300 border-sky-400/30',
                blue:    'bg-blue-500/15 text-blue-300 border-blue-400/30',
                amber:   'bg-amber-500/15 text-amber-300 border-amber-400/30',
              }[c.badgeTone];
              const iconCls = {
                emerald: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
                sky:     'bg-sky-500/20 text-sky-300 border-sky-400/30',
                blue:    'bg-blue-500/20 text-blue-300 border-blue-400/30',
                amber:   'bg-amber-500/20 text-amber-300 border-amber-400/30',
              }[c.badgeTone];
              return (
                <div key={i} data-testid={`mp-advice-${i}`}
                  className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${iconCls}`}>
                      <c.Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm sm:text-[15px] text-white leading-tight" style={{ fontFamily: 'Outfit' }}>{c.t}</h3>
                        <span className={`text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${badgeCls}`}>
                          {c.badge}
                        </span>
                      </div>
                      <p className="text-xs sm:text-[13px] text-white/65 mt-1.5 leading-relaxed">{c.d}</p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        <button
                          data-testid={`mp-advice-${i}-step`}
                          onClick={() => { if (c.badge === 'Installateur nodig') { logEvent('advice_installer_click'); } changeTab('apparaten'); }}
                          className="text-xs px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-full font-semibold transition-colors"
                        >
                          Bekijk stap
                        </button>
                        <button
                          data-testid={`mp-advice-${i}-later`}
                          className="text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/15 text-white/75 rounded-full transition-colors"
                        >
                          Plan later
                        </button>
                        <button
                          data-testid={`mp-advice-${i}-skip`}
                          className="text-xs px-3 py-1.5 text-white/45 hover:text-white/70 transition-colors"
                        >
                          Niet relevant
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === 'feedback' && (
          <div data-testid="mp-tab-feedback">
            {feedbackDone ? (
              <div
                className="rounded-2xl border border-emerald-400/30 shadow-2xl p-6 text-center"
                style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.20) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
                data-testid="pilot-feedback-thanks">
                <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto mb-2" />
                <h3 className="font-semibold text-lg text-white" style={{ fontFamily: 'Outfit' }}>Bedankt voor uw feedback</h3>
              </div>
            ) : (
              <FeedbackForm pilotType={pilot.pilot_type}
                onDone={() => setFeedbackDone(true)} />
            )}
          </div>
        )}

        {tab === 'help' && (
          <div className="space-y-3" data-testid="mp-tab-help">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Outfit' }}>Hulp en uitleg</h2>
            <p className="text-sm text-white/65">Korte antwoorden op de meest gestelde vragen over Mijn Powersure.</p>
            {[
              { q: 'Werkt Powersure zonder meetkoppeling?', a: 'Ja, u ziet voorbeeld-overzichten en bereidt uw setup voor. Voor live cijfers koppelt u later een P1-meter of slimme meter.' },
              { q: 'Hoe schakelt Powersure mijn apparaten?', a: 'Voorlopig niet. Automatische sturing staat uit tot u apart toestemming geeft en de hardware getest is.' },
              { q: 'Kan ik later opzeggen?', a: 'Ja, het abonnement is maandelijks opzegbaar. €9,95 eerste 3 maanden, daarna €14,95 per maand.' },
              { q: 'Welke betaalmethoden zijn beschikbaar?', a: 'Creditcard en iDEAL via Stripe. Voor maandelijkse incasso na iDEAL wordt een SEPA-mandaat aangemaakt.' },
              { q: 'Wat doet Powersure met mijn gegevens?', a: 'Alleen wat nodig is voor uw advies. Geen verkoop aan derden. U kunt gegevens later wissen via Account.' },
            ].map((item, i) => (
              <div key={i} className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-4">
                <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{item.q}</h3>
                <p className="text-sm text-white/70 mt-1.5 leading-relaxed">{item.a}</p>
              </div>
            ))}
            <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-4">
              <p className="text-sm text-white/85">Meer vragen? Powersure breidt de hulpsectie binnenkort uit. Mail-support volgt zodra die live staat.</p>
            </div>
          </div>
        )}

        {tab === 'account' && (
          <div className="space-y-3" data-testid="mp-tab-account">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Outfit' }}>Account &amp; pilot</h2>

            {/* Powersure abonnementskaart — concept-aligned strak prijsblok */}
            <div
              className="rounded-2xl border border-emerald-400/30 p-5 shadow-2xl overflow-hidden relative"
              style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.20) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
              data-testid="mp-pricing-card"
            >
              <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-emerald-300/90">
                Energy Control Monitoring
              </div>
              <div className="flex items-baseline gap-1.5 mt-1.5">
                <span className="text-4xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>€ 9,95</span>
                <span className="text-sm text-white/80">per maand</span>
              </div>
              <div className="text-xs text-white/70 mt-1">Eerste 3 maanden, daarna <strong className="text-white">€ 14,95 per maand</strong></div>
              <ul className="mt-3 space-y-1.5 text-[12.5px] text-white/80">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 mt-0.5 shrink-0" /> Maandelijks opzegbaar</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 mt-0.5 shrink-0" /> Geen leverancierswissel</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 mt-0.5 shrink-0" /> Geen automatische sturing zonder uw OK</li>
              </ul>
              <div className="mt-4 flex items-center gap-2 text-[11px]">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-white/85">Kaartbetaling</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/8 border border-white/10 text-white/60">iDEAL wordt voorbereid</span>
              </div>
            </div>

            <div className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-4 space-y-2">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-white/55">Naam:</span> <strong className="text-white">{pilot.name}</strong></div>
                <div><span className="text-white/55">Type:</span> <span className="text-white/85">{pilot.pilot_type}</span></div>
                <div><span className="text-white/55">Status:</span> <span className="text-white/85">{pilot.status}</span></div>
                <div><span className="text-white/55">Geldig tot:</span> <span className="text-white/85">{pilot.expires_at?.slice(0, 10) || '\u2014'}</span></div>
              </div>
            </div>
            <div className="rounded-2xl bg-emerald-500/5 border border-emerald-400/20 p-4 text-sm space-y-1 text-white/80">
              <p><strong className="text-white">Privacy:</strong> Powersure bewaart geen foto&rsquo;s, serienummers, MAC- of IP-adressen.</p>
              <p>Automatische sturing van uw apparaten staat uit.</p>
              <p>Sturing kan later alleen met uw aparte toestemming.</p>
            </div>

            {/* Fase 3.4 — Mijn device-data zelfservice */}
            <MyDeviceDataPanel
              sessionId={(typeof window !== 'undefined' && window.localStorage?.getItem('ps_session_id')) || undefined}
            />

            {/* Fase 3.6 — Read-only pilot opt-in/out */}
            <PilotOptInCard
              sessionId={(typeof window !== 'undefined' && window.localStorage?.getItem('ps_session_id')) || undefined}
            />
            {!isInstalled && (
              <div className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-4"
                data-testid="mp-account-install-card">
                <h3 className="font-semibold mb-1 text-white text-sm" style={{ fontFamily: 'Outfit' }}>App installeren</h3>
                {installPrompt ? (
                  <>
                    <p className="text-sm text-white/65 mb-3 leading-relaxed">
                      Zet Mijn Powersure op uw beginscherm voor snelle toegang.
                    </p>
                    <button onClick={triggerInstall}
                      data-testid="mp-account-install-btn"
                      className="w-full px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-full text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
                      <Download className="w-4 h-4" /> Installeer Mijn Powersure
                    </button>
                  </>
                ) : installSuppressed ? (
                  <p className="text-sm text-white/65 leading-relaxed" data-testid="mp-install-manual-hint">
                    Open Mijn Powersure in uw browser of zet de pagina op uw beginscherm
                    via het browsermenu (drie puntjes &rarr; &ldquo;Pagina toevoegen aan&rdquo;
                    of &ldquo;Toevoegen aan startscherm&rdquo;).
                  </p>
                ) : (
                  <p className="text-sm text-white/65 leading-relaxed">
                    Op iPhone/iPad: tik op de deel-knop in Safari en kies &ldquo;Zet op beginscherm&rdquo;.
                    Op Android Chrome: open het menu en kies &ldquo;App installeren&rdquo;.
                  </p>
                )}
              </div>
            )}
            {isInstalled && (
              <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-4 text-sm flex items-center gap-2 text-white/85"
                data-testid="mp-account-installed">
                <CheckCircle2 className="w-5 h-5 text-emerald-300 flex-shrink-0" />
                <span>Mijn Powersure is geïnstalleerd op dit apparaat.</span>
              </div>
            )}
            <button onClick={() => {
                localStorage.removeItem(PILOT_TOKEN_KEY);
                navigate('/');
              }}
              data-testid="mp-logout"
              className="w-full px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/15 rounded-full text-sm font-medium text-white/85 transition-colors">
              Uitloggen
            </button>
          </div>
        )}

        <p className="text-xs text-white/40 text-center pt-3">
          Pilot geldig tot {pilot.expires_at?.slice(0, 10) || '\u2014'}
        </p>
      </main>

      <AppBottomNav tab={tab} onChange={changeTab} />

      {/* P5: Onboarding-sheet — alleen binnen /app, na app-open */}
      {showSetup && (
        <AppOnboardingSetup
          pilotName={pilot?.name}
          onClose={() => setShowSetup(false)}
          onCompleted={() => { setSetupDone(true); setShowSetup(false); logEvent('app_onboarding_completed'); }}
        />
      )}
    </div>
    </AppSidebar>
  );
}
